import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { AppMode } from "../../../constants";
import PWAInstallation from "../PWAInstallation";
import { isDesktopViewport } from "../../../helpers/viewport.js";
import { PersistentFooterContext } from "./persistentFooterContext";
import "./footer.css";

const ICONS_LIGHT = {
    home:       "/assets/img/footer/home-light.svg",
    categories: "/assets/img/footer/categories-light.svg",
    cart:       "/assets/img/footer/cart-light.svg",
    wishlist:   "/assets/img/footer/wishlist-light.svg",
    me:         "/assets/img/footer/me-light.svg",
};
const ICONS_DARK = {
    home:       "/assets/img/footer/home-dark.svg",
    homeInactive: "/assets/img/footer/home-inactive-dark.svg",
    categories: "/assets/img/footer/categories-dark.svg",
    cart:       "/assets/img/footer/cart-dark.svg",
    wishlist:   "/assets/img/footer/wishlist-dark.svg",
    me:         "/assets/img/footer/me-dark.svg",
};

const FONT = "'Montserrat', sans-serif";

/** Pink highlight for active tab icons (Sprint + Relay footers). */
const ACTIVE_ICON_FILTER =
    "brightness(0) saturate(100%) invert(52%) sepia(61%) saturate(2876%) hue-rotate(298deg) brightness(98%) contrast(92%)";

const ACTIVE_LABEL_DARK = "#ED4A89";
const ACTIVE_LABEL_LIGHT = "#E14685";
const INACTIVE_LABEL_DARK = "#6b6389";
const INACTIVE_LABEL_LIGHT = "#9ca3af";

/**
 * Compact tab chrome (icon + label). Home-indicator inset is applied separately
 * via padding-bottom so we don't stack empty space under the labels.
 * Exported for pages that reserve scroll space without rendering Footer twice.
 */
export const FOOTER_CONTENT_HEIGHT_PX = 52;
export const FOOTER_SAFE_BOTTOM = "max(4px, env(safe-area-inset-bottom, 0px))";
export const FOOTER_TOTAL_HEIGHT = `calc(${FOOTER_CONTENT_HEIGHT_PX}px + ${FOOTER_SAFE_BOTTOM})`;

class Footer extends Component {
    static contextType = PersistentFooterContext;

    state = {
        isFooterVisible: true,
        lastScrollY: 0,
    };

    isRelay = () => this.props.appMode === AppMode.RELAY;

    getCartCount = () => {
        const { cartProducts, phase2CartProducts, cartTotal } = this.props;
        const products = this.isRelay() ? phase2CartProducts : cartProducts;

        if (localStorage.getItem("countQuantityAsTotalItemsOnCart") === "true") {
            return products.reduce((sum, item) => sum + item.quantity, 0);
        }
        if (this.isRelay()) return phase2CartProducts.length;
        return cartTotal ? cartTotal.productQuantity : 0;
    };

    /** Routes where the bottom nav should stay visible (no scroll-hide). */
    isPrimaryNavRoute = () => {
        const path = (this.props.location.pathname || "").toLowerCase();
        if (path === "/" || path === "/relax" || path === "/phase2") return true;
        if (path.includes("/cart")) return true;
        if (path.includes("/wishlist") || path.includes("/my-favorite")) return true;
        if (path.includes("/my-account") || path.includes("/edit-profile")) return true;
        if (path.includes("/my-orders") || path.includes("/my-addresses") || path.includes("/alerts")) return true;
        if (path.includes("/categories") || path.includes("/relax/categories") || path.includes("/phase2/categories")) return true;
        if (path.includes("/explore")) return true;
        return false;
    };

    getActiveTab = () => {
        const {
            active_tab,
            active_account,
            active_cart,
            active_explore,
        } = this.props;

        if (active_tab) return active_tab;
        if (active_cart) return "cart";
        if (active_account) return "me";
        if (active_explore) return "categories";

        const path = (this.props.location.pathname || "").toLowerCase();

        if (path.includes("/cart")) return "cart";
        if (path.includes("/my-wishlist") || path.includes("/my-favorite") || path.includes("/wishlist")) {
            return "wishlist";
        }
        if (
            path.includes("/my-account") ||
            path.includes("/edit-profile") ||
            path.includes("/my-orders") ||
            path.includes("/my-addresses") ||
            path.includes("/alerts") ||
            path.includes("/account")
        ) {
            return "me";
        }
        if (path.includes("/categories") || path.includes("/relax/categories") || path.includes("/phase2/categories") || path.includes("/explore")) {
            return "categories";
        }
        if (path === "/relax" || path === "/phase2" || path.startsWith("/relax/home") || path.startsWith("/phase2/home")) return "home";
        return "home";
    };

    handleScroll = () => {
        const currentScrollY = window.scrollY;

        if (this.isPrimaryNavRoute()) {
            this.setState({ isFooterVisible: true, lastScrollY: currentScrollY });
            return;
        }

        const cartCount = this.getCartCount();
        if (
            cartCount > 0 ||
            (window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100
        ) {
            this.setState({ isFooterVisible: true, lastScrollY: currentScrollY });
            return;
        }

        this.setState({
            isFooterVisible: currentScrollY <= 100,
            lastScrollY: currentScrollY,
        });
    };

    componentDidMount() {
        window.addEventListener("scroll", this.handleScroll, { passive: true });
        this.setState({ isFooterVisible: true });
        this.handleScroll();
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.handleScroll);
    }

    componentDidUpdate(prevProps) {
        const prev = this.getCartCountFromProps(prevProps);
        const curr = this.getCartCount();
        if (curr > 0 && prev === 0) {
            this.setState({ isFooterVisible: true });
        }

        if (prevProps.location.pathname !== this.props.location.pathname) {
            this.setState({ isFooterVisible: true });
            this.handleScroll();
        }
    }

    getCartCountFromProps = (props) => {
        const { cartProducts, phase2CartProducts, cartTotal, appMode } = props;
        const isRelayMode = appMode === AppMode.RELAY;
        const products = isRelayMode ? phase2CartProducts : cartProducts;
        if (localStorage.getItem("countQuantityAsTotalItemsOnCart") === "true") {
            return products.reduce((sum, item) => sum + item.quantity, 0);
        }
        if (isRelayMode) return phase2CartProducts.length;
        return cartTotal ? cartTotal.productQuantity : 0;
    };

    getIconStyle = (tabKey, isActive, dark) => {
        if (isActive) {
            if (dark && tabKey === "home") {
                return { filter: "none", opacity: 1 };
            }
            return { filter: ACTIVE_ICON_FILTER, opacity: 1 };
        }
        return {
            filter: "none",
            opacity: dark ? 0.5 : 0.55,
        };
    };

    getTabIcon = (tab, isActive, dark) => {
        if (dark && tab.key === "home") {
            return isActive ? ICONS_DARK.home : ICONS_DARK.homeInactive;
        }
        const ICONS = dark ? ICONS_DARK : ICONS_LIGHT;
        return ICONS[tab.key];
    };

    render() {
        const usePersistentSpacer =
            this.context &&
            !this.props.persistentChrome &&
            !isDesktopViewport();
        if (usePersistentSpacer) {
            return <div className="app-footer-spacer" aria-hidden="true" />;
        }

        const relay = this.isRelay();
        const dark = !relay;
        const activeTab = this.getActiveTab();
        const cartCount = this.getCartCount();
        const ICONS = dark ? ICONS_DARK : ICONS_LIGHT;
        const onCartPage = (this.props.location.pathname || "").includes("/cart");

        const tabs = [
            {
                key:      "home",
                label:    "Home",
                icon:     ICONS.home,
                to:       relay ? "/relax" : "/",
            },
            {
                key:      "categories",
                label:    "Categories",
                icon:     ICONS.categories,
                to:       relay ? "/relax/categories" : "/categories/stores",
            },
            {
                key:      "cart",
                label:    "Cart",
                icon:     ICONS.cart,
                to:       "/cart",
                badge:    cartCount > 0 ? cartCount : null,
                disabled: cartCount === 0 && onCartPage,
            },
            {
                key:      "wishlist",
                label:    "Wishlist",
                icon:     ICONS.wishlist,
                to:       "/my-wishlist",
            },
            {
                key:      "me",
                label:    "Me",
                icon:     ICONS.me,
                to:       "/my-account",
            },
        ];

        const footerBg = dark
            ? "linear-gradient(-0.6deg, rgb(5, 8, 39) 14.114%, rgb(66, 0, 69) 99.807%)"
            : "#fff";

        const activeLabelColor = dark ? ACTIVE_LABEL_DARK : ACTIVE_LABEL_LIGHT;
        const inactiveLabelColor = dark ? INACTIVE_LABEL_DARK : INACTIVE_LABEL_LIGHT;

        return (
            <React.Fragment>
                {localStorage.getItem("showPwaInstallPromptFooter") === "true" && (
                    <PWAInstallation type="footer" />
                )}

                {/* Pages inside AnimatedSwitch already reserve this space. */}
                {!this.props.persistentChrome ? (
                    <div className="app-footer-spacer" aria-hidden="true" />
                ) : null}

                <nav
                    className={`app-footer${dark ? " app-footer--dark" : " app-footer--light"}${
                        this.state.isFooterVisible ? " app-footer--visible" : " app-footer--hidden"
                    }`}
                    role="navigation"
                    aria-label="Main navigation"
                    style={{ background: footerBg }}
                >
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.key;
                        const iconStyle = this.getIconStyle(tab.key, isActive, dark);
                        return (
                            <Link
                                key={tab.key}
                                to={{
                                    pathname: tab.to,
                                    state: { skipPageTransition: true },
                                }}
                                className={`app-footer__tab${isActive ? " app-footer__tab--active" : ""}`}
                                aria-current={isActive ? "page" : undefined}
                                onClick={tab.disabled ? (e) => e.preventDefault() : undefined}
                                style={{
                                    cursor: tab.disabled ? "default" : "pointer",
                                }}
                            >
                                <span className="app-footer__icon-slot">
                                    <img
                                        src={this.getTabIcon(tab, isActive, dark)}
                                        alt=""
                                        aria-hidden
                                        className="app-footer__icon"
                                        style={iconStyle}
                                    />
                                    {tab.badge ? (
                                        <span className="app-footer__badge" style={{ fontFamily: FONT }}>
                                            {tab.badge}
                                        </span>
                                    ) : null}
                                </span>
                                <span
                                    className="app-footer__label"
                                    style={{
                                        fontFamily: FONT,
                                        fontWeight: isActive ? 700 : 500,
                                        color: isActive ? activeLabelColor : inactiveLabelColor,
                                    }}
                                >
                                    {tab.label}
                                </span>
                            </Link>
                        );
                    })}
                </nav>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state, ownProps) => ({
    cartTotal:          state.total.data,
    cartProducts:       state.cart.products,
    phase2CartProducts: state.cart.phase2Products || [],
    appMode:            ownProps.appMode || state.settings.appMode,
});

export default withRouter(connect(mapStateToProps)(Footer));
